import "@ncp/ui/fonts";
import "./index.css";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { I18nProvider, addLoadedBundle, createI18nLean } from "@ncp/i18n";
import { App } from "./App";
import { SHELL } from "./app/flags";
import { setLocalesReady } from "./app/locales-ready";
import bootEn from "./locales/boot.en";

// The catalogues load on their own (code-split, ECONIC bundle budget SH-22). The main bundle carries the shared English catalogue and the
// frame's own strings (locales/boot.en.ts). The rest of the English catalogue loads right after the first render, and every screen waits
// for it with its chunk (app/locales-ready.ts). Arabic — the app's strings and the shared Arabic catalogue — loads before the first render
// when the person reads Arabic, else right after it (bilingual screens show the other language's names, so they arrive within a moment).
// The `os` and `legacy` frames (flags until 29 Oct) draw strings outside the frame's set, so under them English is in before the first render.
const loadEnglish = () => import("./locales/en").then((m) => m.default);
const loadArabic = () => Promise.all([import("./locales/ar").then((m) => m.default), import("@ncp/i18n/ar-common").then((m) => m.default)]);

/** The boot language, by the same rule as @ncp/i18n (and public/econic-boot.js): the saved choice, else the browser's. */
function bootLocale(): "en" | "ar" {
  try {
    const stored = window.localStorage.getItem("ncp.locale");
    if (stored === "en" || stored === "ar") return stored;
  } catch {
    // storage unavailable: the browser's language decides
  }
  return (window.navigator.language ?? "").toLowerCase().startsWith("ar") ? "ar" : "en";
}

async function boot() {
  const arabicFirst = bootLocale() === "ar";
  const englishFirst = SHELL !== "econic";
  const english = loadEnglish();
  const [arabic, fullEnglish] = await Promise.all([arabicFirst ? loadArabic().catch(() => null) : null, englishFirst ? english.catch(() => null) : null]);
  const i18n = createI18nLean({
    resources: {
      en: { assistant: fullEnglish ?? bootEn },
      ...(arabic ? { ar: { assistant: arabic[0], common: arabic[1] } } : {}),
    },
  });
  // Screens re-render when the other language's bundle is added (react-i18next listens to the store's "added" event).
  i18n.options.react = { ...(i18n.options.react ?? {}), bindI18nStore: "added" };
  // The chat's guardrail badge names a turn the language model did not complete (`ai.flags.<flag>`); the shared catalogue has no word for
  // it yet, so it read "Model Unavailable" in both languages. Added, never overwriting a word the shared catalogue already has.
  i18n.addResourceBundle("en", "common", { ai: { flags: { ModelUnavailable: "language model unavailable" } } }, true, false);
  i18n.addResourceBundle("ar", "common", { ai: { flags: { ModelUnavailable: "النموذج اللغوي غير متاح" } } }, true, false);

  // The rest of English: an English reader's screens wait for it; an Arabic reader's screens already have their own language in full.
  const englishIn = fullEnglish ? Promise.resolve() : english.then((en) => addLoadedBundle(i18n, "en", "assistant", en)).catch(() => undefined);
  setLocalesReady(arabic ? Promise.resolve() : englishIn);

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <I18nProvider i18n={i18n}>
        <App />
      </I18nProvider>
    </StrictMode>,
  );

  if (!arabic) {
    window.setTimeout(() => {
      void loadArabic()
        .then(([app, common]) => {
          addLoadedBundle(i18n, "ar", "common", common);
          addLoadedBundle(i18n, "ar", "assistant", app);
        })
        .catch(() => undefined);
    }, 0);
  }
}

void boot();
